import { EventListenerCallback, useIsFocused } from '@react-navigation/native';
import { useFormikContext } from 'formik';
import React, { useContext, useEffect, useRef } from 'react';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { getIntl } from '../../utils/intl';
import { Alert, Platform } from 'react-native';

const intl = getIntl('FormExitValidation', {
    confirmationTitle: 'Discard Changes?',
    confirmationMessage: 'You have unsaved changes. Are you sure to discard them and leave the screen?',
    confirmationCancel: "Don't Leave",
    confirmationConfirm: 'Discard',
});

type IsFormDirtyContextValue = {
    isFormDirty: boolean;
    setIsFormDirty: (value: boolean) => void;
};

const IsFormDirtyContext = React.createContext<IsFormDirtyContextValue>({
    isFormDirty: false,
    setIsFormDirty: () => {},
});

export const useIsFormDirty = () => {
    return useContext(IsFormDirtyContext);
};

const IsFormDirtyProvider: React.FC<{ children: any }> = ({ children }) => {
    const [isFormDirty, setIsFormDirty] = React.useState(false);

    return (
        <IsFormDirtyContext.Provider value={{ isFormDirty, setIsFormDirty }}>{children}</IsFormDirtyContext.Provider>
    );
};

/**
 * return as HOC since base app component needs access to the inner context hook.  If not using an HOC, the base app component
 * would need to be broken into a couple components to get access to the context hook.
 */
export function withIsFormDirtyProvider<TProps>(Component: React.FC<TProps>) {
    return (props: any) => (
        <IsFormDirtyProvider>
            <Component {...props} />
        </IsFormDirtyProvider>
    );
}

/**
 * Component to be placed inside of form to notify root provider of a form dirty state change
 */
export const FormDirtyStateTracker: React.FC = () => {
    const formikContext = useFormikContext();
    const { setIsFormDirty } = useIsFormDirty();
    const isFocused = useIsFocused();

    React.useEffect(() => {
        const isDirty = formikContext.dirty && isFocused && !formikContext.isSubmitting;
        setIsFormDirty(isDirty);

        return () => {
            setIsFormDirty(false);
        };
    }, [formikContext.dirty, setIsFormDirty, isFocused, formikContext.isSubmitting]);

    return null;
};

/**
 * If used on a screen, will prevent the user from navigating away from the screen if the form is dirty.
 *
 * This is currently only working on mobile.  On web, the user can still navigate away from the screen
 */
export function useConfirmBackNavigationOnDirtyForm(navigation: NativeStackNavigationProp<any>) {
    const callback = useRef<EventListenerCallback<any, any> | null>(null);
    const { isFormDirty } = useIsFormDirty();

    useEffect(() => {
        if (callback.current && Platform.OS === 'web') {
            navigation.removeListener('beforeRemove', callback.current);
        }

        callback.current = (e: any) => {
            if (!isFormDirty || Platform.OS !== 'web') {
                return;
            }

            e?.preventDefault?.();

            Alert.alert(intl('confirmationTitle'), intl('confirmationMessage'), [
                {
                    text: intl('confirmationCancel'),
                    style: 'cancel',
                    onPress: () => {},
                },
                {
                    text: intl('confirmationConfirm'),
                    style: 'destructive',
                    onPress: () => {
                        navigation.dispatch(e?.data?.action);
                    },
                },
            ]);
        };

        navigation.addListener('beforeRemove', callback.current);

        return () => {
            if (callback.current && Platform.OS === 'web') {
                navigation.removeListener('beforeRemove', callback.current);
            }
        };
    }, [isFormDirty, navigation]);
}
